import React, { useCallback, useEffect, useState, useMemo } from "react";
import IbDataGrid from "./IbDataGrid";
import { getUserRole } from "../../utils/utils";
import { ROLES, IB_EDIT_CUST_RATES } from "../../constants/constant";
import ToastMessageFunction from "../../common/toast-message-function";
import { GridCellModes } from "@mui/x-data-grid";
import { TextField } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";

import { putDataWAuth } from "../../utils/api-service";
import { fetchCustomRates } from "../../store/slices/interestBearing/customRatesReducer";

const CustomRates = (props) => {
  const dispatch = useDispatch();
  const customRatesState = useSelector((state) => state.customRatesState);
  const userData = useSelector((state) => state.userState.userData);

  const {
    isParentLocked,
    handleCustomRatesEditedData,
    selectedMonth,
    selectedCountry,
    startEndDataAndFisMonthMap,
    countryCodeMap,
  } = props;

  const toastManager = ToastMessageFunction();
  const [cellModesModel, setCellModesModel] = useState({});
  const [editedCustomRates, setEditedCustomRates] = useState([]);
  const [shouldRefresh, setShouldRefresh] = useState(false);

  const getCustomRowId = (row) => row.customRateid;

  const showToast = useCallback(
    (message, severity) => {
      toastManager.getToast(message, severity);
    },
    [toastManager]
  );

  const getCustomRateTypeList = (selectedCountry, selectedMonth) => {
    const params = {
      countryCode: countryCodeMap[selectedCountry],
      fisMonthYear: startEndDataAndFisMonthMap[selectedMonth],
    };
    dispatch(fetchCustomRates(params));
  };

  const handleEditChange = (data, newValue) => {
    setEditedCustomRates((prev) => {
      const updatedRates = [...prev];
      const existingIndex = updatedRates.findIndex(
        (item) => item.customRateid === data.customRateid
      );
      const updatedData = {
        ...data,
        rate: newValue,
      };
      if (existingIndex > -1) {
        updatedRates[existingIndex] = updatedData;
      } else {
        updatedRates.push(updatedData);
      }
      return updatedRates;
    });
  };

  const renderEditInputCell = (params) => {
    return params.id ? (
      <TextField
        value={params.value}
        onChange={(event) => {
          const inputValue = event.target.value;
          const regex = /^(0|[1-9]\d*)(\.\d{0,2})?$/;
          if (regex.test(inputValue) || inputValue === "") {
            handleEditChange(params.row, event.target.value);
            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: event.target.value,
            });
          }
        }}
        fullWidth
        autoFocus
      />
    ) : (
      params.value
    );
  };

  useEffect(() => {
    const newCellModesModel = {};
    customRatesState?.customRatesData?.forEach((row) => {
      newCellModesModel[row.customRateid] = {
        rate: {
          mode:
            getUserRole(userData) === ROLES.CCM && !isParentLocked
              ? GridCellModes.Edit
              : GridCellModes.View,
        },
      };
    });
    setCellModesModel(newCellModesModel);
  }, [customRatesState?.customRatesData, userData, isParentLocked]);

  useEffect(() => {
    getCustomRateTypeList(selectedCountry, selectedMonth);
  }, [selectedCountry, selectedMonth]);

  useEffect(() => {
    handleCustomRatesEditedData(editedCustomRates);
  }, [editedCustomRates, handleCustomRatesEditedData]);

  // Refresh data after successful save
  useEffect(() => {
    if (shouldRefresh && !customRatesState?.isLoading) {
      const params = {
        countryCode: countryCodeMap[selectedCountry],
        fisMonthYear: startEndDataAndFisMonthMap[selectedMonth],
      };
      dispatch(fetchCustomRates(params));
      setShouldRefresh(false);
    }
  }, [shouldRefresh, customRatesState?.isLoading, selectedCountry, selectedMonth, dispatch]);

  useEffect(() => {
    if (customRatesState?.error) {
      showToast(customRatesState.error, "error");
    }
  }, [customRatesState?.error, showToast]);

  const columns = useMemo(() => [
    {
      field: "customRateType",
      width: 260,
      editable: false,
      renderHeader: () => <strong>Rate Type</strong>,
    },
    {
      field: "currency",
      width: 200,
      editable: false,
      renderHeader: () => <strong>Currency</strong>,
    },
    {
      field: "rate",
      width: 200,
      editable: true,
      renderHeader: () => <strong>Rate (%)</strong>,
      renderEditCell: renderEditInputCell,
    },
  ], []);

  const handleSaveCustomRates = async () => {
    if (!editedCustomRates || editedCustomRates.length === 0) {
      showToast("No changes to save", "info");
      return;
    }

    // Convert rate values to numbers
    const payload = editedCustomRates.map(rate => ({
      customRateid: rate.customRateid,
      customRateType: rate.customRateType,
      currency: rate.currency,
      rate: rate.rate !== null && rate.rate !== "" ? Number(rate.rate) : null,
    }));

    try {
      const response = await putDataWAuth(IB_EDIT_CUST_RATES, payload);
      if (response && response.data) {
        showToast(response.data.message || "Custom rates saved successfully", "success");
        setEditedCustomRates([]);
        setShouldRefresh(true);
      }
    } catch (error) {
      showToast(
        error.response?.data?.message || "Failed to save custom rates!",
        "error"
      );
    }
  };

  return (
    <div style={{ width: "100%" }}>
      {toastManager.toast}
      <IbDataGrid
        loading={customRatesState?.isLoading}
        editMode="batch"
        getRowId={getCustomRowId}
        columns={columns}
        rows={customRatesState?.customRatesData || []}
        cellModesModel={cellModesModel}
        onSave={handleSaveCustomRates}
        initialState={{
          sorting: {
            sortModel: [{ field: "customRateType", sort: "asc" }],
          },
        }}
      />
    </div>
  );
};

export default CustomRates;